import { useLang } from '../context/LanguageContext';
import LegalPageShell from '../components/LegalPageShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { LEGAL_TE_SUMMARIES } from '../content/legalContent';
import { BUSINESS, businessAddressBlock, businessPhoneDisplay, businessTelHref } from '../config/business';

export function PrivacyContent() {
  return (
    <>
      <p className="legal-updated">Last updated: September 2026</p>

      <p className="legal-highlight">
        We respect your privacy. This policy covers the website and the Repatikosam iOS app. Payment card details
        are handled by Razorpay — we never store them. Questions:{' '}
        <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
      </p>

      <div className="legal-section">
        <h3>1. Information We Collect</h3>
        <p>We collect the following information when you use our services:</p>
        <ul>
          <li>Name and email address from Google sign-in</li>
          <li>Payment status and transaction IDs (processed securely via Razorpay — we do not store card details)</li>
          <li>Usage data such as pages visited and content read</li>
          <li>Device information and IP address</li>
        </ul>
      </div>

      <div className="legal-section">
        <h3>2. How We Use Your Information</h3>
        <p>We use your information to:</p>
        <ul>
          <li>Provide and manage your subscription</li>
          <li>Send important service notifications via email</li>
          <li>Improve our content and services</li>
          <li>Process payments and prevent fraud</li>
        </ul>
      </div>

      <div className="legal-section">
        <h3>3. Data Sharing</h3>
        <p>We do not sell or rent your personal data to third parties. We may share data with:</p>
        <ul>
          <li>
            <strong>Razorpay</strong> — for payment processing (including international card payments where
            supported)
          </li>
          <li>
            <strong>Google</strong> — for authentication (OAuth)
          </li>
          <li>Legal authorities if required by law</li>
        </ul>
      </div>

      <div className="legal-section">
        <h3>4. International Customers &amp; Cross-Border Data</h3>
        <p>
          If you access Repatikosam from outside India, your information may be stored and processed in India, where
          our servers and payment partner (Razorpay) operate. By using our services, you consent to this transfer
          where permitted by applicable law.
        </p>
        <p>
          Razorpay handles card and payment data according to PCI-DSS standards. We receive only transaction status,
          payment IDs, and billing metadata — never your full card number.
        </p>
      </div>

      <div className="legal-section">
        <h3>5. Data Security</h3>
        <p>
          We implement industry-standard security measures to protect your data. All payment transactions are
          encrypted using SSL/TLS technology.
        </p>
      </div>

      <div className="legal-section">
        <h3>6. Cookies & Analytics</h3>
        <p>
          We use cookies to maintain your session and language preferences. We may use analytics tools (e.g. Google
          Analytics, Meta Pixel) to understand site usage. You can disable cookies in your browser settings,
          though this may affect functionality.
        </p>
      </div>

      <div className="legal-section">
        <h3>7. Your Rights</h3>
        <p>You have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your account and data</li>
        </ul>
        <p>
          To exercise these rights, contact us at <strong>{BUSINESS.email}</strong> or use Contact in the iOS app
          (Menu → Contact). Account deletion requests from your registered Google email are completed within 30
          days, except records we must retain for tax or legal compliance.
        </p>
      </div>

      <div className="legal-section">
        <h3>8. Children&apos;s Privacy</h3>
        <p>Our services are not directed to children under 13. We do not knowingly collect data from children.</p>
      </div>

      <div className="legal-section">
        <h3>9. Changes to This Policy</h3>
        <p>We may update this policy periodically. Significant changes will be reflected on this page with an updated date.</p>
      </div>

      <div className="legal-section">
        <h3>10. Contact</h3>
        <p>
          <strong>{BUSINESS.email}</strong> ·{' '}
          <a href={businessTelHref()}>{businessPhoneDisplay()}</a>
          <br />
          {businessAddressBlock()}
        </p>
        <p>
          <strong>{BUSINESS.legalName}</strong> · GSTIN: {BUSINESS.gstin}
        </p>
      </div>
    </>
  );
}

export default function PrivacyPage({ onClose, standalone = false }) {
  const { t } = useLang();
  usePageMeta({
    title: `${t.footerPrivacy} | ${t.brandName}`,
    description: `Privacy policy for Repatikosam by M S Publications.`,
    path: '/privacy',
    enabled: standalone,
  });
  return (
    <LegalPageShell
      title={t.footerPrivacy}
      onClose={onClose}
      standalone={standalone}
      teSummary={LEGAL_TE_SUMMARIES.privacy}
    >
      <PrivacyContent />
    </LegalPageShell>
  );
}
